import { AdminApiDal } from '../adminApiDal';
import { ApiResponseBase } from '../../../models/apiResponseBase';
import { inject, injectable } from '../../../../modules/common/di';
import { GetOrganizationSurveysApiResponse } from './getOrganizationSurveysApiResponse';
import { OrganizationSurveyModel } from './createOrganizationSurveyApiResponse';
import { UpdateOrganizationSurveyModel } from './updateOrganizationSurveyApiResponse';
import { GetOrganizationSurveyQuestionsApiResponse } from './getOrganizationSurveyQuestionsApiResponse';

/**
 * Organization surveys management.
 * See {@link https://app.peoplepowerco.com/cloud/apidocs/admin.html#tag/Surveys}
 */
@injectable('SurveysApi')
export class SurveysApi {
  @inject('AdminApiDal') protected readonly dal!: AdminApiDal;

  /**
   * Return a list of surveys available for an organization or parent organizations.
   * See {@link https://app.peoplepowerco.com/cloud/apidocs/admin.html#tag/Surveys/operation/Get%20Organization%20Surveys}
   *
   * @param {number} organizationId Organization ID.
   * @returns {Promise<GetOrganizationSurveysApiResponse>}
   */
  getOrganizationSurveys(organizationId: number): Promise<GetOrganizationSurveysApiResponse> {
    return this.dal.get(`organizations/${encodeURIComponent(organizationId.toString())}/surveys`);
  }

  /**
   * Create new organization survey.
   * See {@link https://app.peoplepowerco.com/cloud/apidocs/admin.html#tag/Surveys/operation/Create%20Organization%20Survey}
   *
   * @param {number} organizationId Organization ID.
   * @param {OrganizationSurveyModel} model Survey model.
   * @returns {Promise<ApiResponseBase>}
   */
  createOrganizationSurvey(organizationId: number, model: OrganizationSurveyModel): Promise<ApiResponseBase> {
    return this.dal.post(`organizations/${encodeURIComponent(organizationId.toString())}/surveys`, model);
  }

  /**
   * Update existing organization survey.
   * All existing questions must be included in the request; missing questions will cause an error.
   * New sections can be added and existing sections can be removed. New questions cannot be added; unknown question keys will cause an error
   * See {@link https://app.peoplepowerco.com/cloud/apidocs/admin.html#tag/Surveys/operation/Update%20Organization%20Survey}
   *
   * @param {number} organizationId Organization ID.
   * @param {string} surveyKey Unique survey key.
   * @param {UpdateOrganizationSurveyModel} model Survey model.
   * @returns {Promise<ApiResponseBase>}
   */
  updateOrganizationSurvey(organizationId: number, surveyKey: string, model: UpdateOrganizationSurveyModel): Promise<ApiResponseBase> {
    return this.dal.put(`organizations/${encodeURIComponent(organizationId.toString())}/surveys/${encodeURIComponent(surveyKey)}`, model);
  }

  /**
   * Return specific survey details, sections, and questions.
   * See {@link https://app.peoplepowerco.com/cloud/apidocs/admin.html#tag/Surveys/operation/Get%20Organization%20Survey%20Questions}
   *
   * @param {number} organizationId Organization ID.
   * @param {string} surveyKey Unique survey key.
   * @returns {Promise<GetOrganizationSurveyQuestionsApiResponse>}
   */
  getOrganizationSurveyQuestions(organizationId: number, surveyKey: string): Promise<GetOrganizationSurveyQuestionsApiResponse> {
    return this.dal.get(`organizations/${encodeURIComponent(organizationId.toString())}/surveys/${encodeURIComponent(surveyKey)}`);
  }

  /**
   * Delete an organization survey by its key.
   * See {@link https://app.peoplepowerco.com/cloud/apidocs/admin.html#tag/Surveys/operation/Delete%20Organization%20Survey}
   *
   * @param {number} organizationId Organization ID.
   * @param {string} surveyKey Unique survey key.
   * @returns {Promise<ApiResponseBase>}
   */
  deleteOrganizationSurvey(organizationId: number, surveyKey: string): Promise<ApiResponseBase> {
    return this.dal.delete(`organizations/${encodeURIComponent(organizationId.toString())}/surveys/${encodeURIComponent(surveyKey)}`);
  }
}
